/**
 * A recipe's step timeline and the two numbers derived from it (#850).
 *
 * « Préparation » (`handsOnMinutes`) is the time the cook's hands are busy;
 * « Prêt en » (`readyInMinutes`) is the elapsed time to the table, waits
 * included. A model writes the segments; these two numbers are arithmetic over
 * them, never a second guess. `checkTimeline` decides whether a model-written
 * timeline may be stored, against what the recipe's own steps and page state.
 *
 * Pure: no imports beyond the stated-waits finder, so api/_lib can use it too.
 */
import { findStatedWaits, findWrittenWaits } from './statedWaits';

export type SegmentKind = 'active' | 'wait-away' | 'wait-home';
export type WaitLabel =
  | 'marinade' | 'fridge' | 'rise' | 'rest' | 'soak' | 'cure' | 'freeze'
  | 'oven' | 'simmer' | 'slow-cooker' | 'other';

export interface TimelineSegment {
  /** Index of the step in the recipe's step list. */
  step: number;
  kind: SegmentKind;
  /** Minutes. */
  min: number;
  max: number;
  /** Runs alongside the previous non-overlapping segment (preheating during a marinade). */
  overlapsPrevious: boolean;
  wait?: WaitLabel;
}

export interface RecipeTimeline {
  version: string;
  segments: TimelineSegment[];
  ahead?: { fr: string; en: string };
}

/** Who stands behind the stored timeline: the checks (`auto`), a reviewer queue (`review`), a person. */
export type TimelineStatus = 'auto' | 'review' | 'person';
export const isTimelineStatus = (v: unknown): v is TimelineStatus => v === 'auto' || v === 'review' || v === 'person';

export interface DerivedTimes {
  handsOnMinutes: number;
  readyInMinutes: number;
  readyInMinutesMin: number;
  moments: number;
  longestWait: { kind: 'wait-away' | 'wait-home'; min: number; max: number; wait: WaitLabel } | null;
}

export type TimelineCheckCode = 'wait_missed' | 'wait_unstated' | 'under_stated' | 'over_without_wait' | 'hands_on_exceeds_ready';
export interface TimelineCheck { code: TimelineCheckCode; detail: string }

/** A stated wait counts as missed above this many times the longest wait the timeline kept. */
const MISSED_WAIT_RATIO = 1.25;
/**
 * A wait this long must be written in the recipe, on its step or the step beside
 * it (stored steps are sometimes cut mid-sentence), and within this ratio of what
 * is written. Below it, a wait is the model's fair reading of an unprinted time.
 */
const WRITTEN_WAIT_MIN_MINUTES = 30;
const WRITTEN_WAIT_REACH_STEPS = 1;
const WRITTEN_WAIT_RATIO = 1.25;
/** Under the page's total by more than this is under-stated. */
const UNDER_STATED_SLACK_MIN = 10;
/** A timeline with no stated wait may run past the page's total by a factor of 2 or by 60 min. */
const OVER_FACTOR = 2;
const OVER_SLACK_MIN = 60;
/** Same floor as findStatedWaits: a shorter duration is a cooking time, not a wait. */
const MIN_WAIT_MINUTES = 10;

/** A marinade whose long end passes this many times its short end has no useful middle. */
const WIDE_MARINADE_RATIO = 4;

/**
 * What a segment adds to « Prêt en ». The cook chooses how long a marinade runs,
 * so the number is its mean and the caption gives the real range; on a very wide
 * range (1 to 24 h) the mean describes nobody, and the number is the recipe's
 * stated minimum. Everything else counts at its long end: a dish is not ready
 * before the slow cooker, the rise or the oven has finished.
 */
function readyLength(s: TimelineSegment): number {
  if (s.wait !== 'marinade') return s.max;
  const wide = s.min > 0 && s.max > WIDE_MARINADE_RATIO * s.min;
  return wide ? s.min : Math.round((s.min + s.max) / 2);
}

/**
 * Waits the recipe never wrote. The prompt says not to invent them, but the same
 * recipe differs between runs (a salad at 264 min, then 4339), and over is never
 * a miss in the eval, so this is the check that stops them reaching a cook.
 */
function unstatedWaits(segments: TimelineSegment[], steps: unknown[]): TimelineSegment[] {
  const written = findWrittenWaits(steps);
  return segments.filter(s =>
    s.kind !== 'active'
    && s.max >= WRITTEN_WAIT_MIN_MINUTES
    && !written.some(w => Math.abs(w.step - s.step) <= WRITTEN_WAIT_REACH_STEPS && w.max * WRITTEN_WAIT_RATIO >= s.max));
}

/** Elapsed minutes: an overlapping segment only adds what outlasts the segment it runs beside. */
function elapsed(segments: TimelineSegment[], pick: (s: TimelineSegment) => number): number {
  let total = 0;
  let previous = 0;
  for (const s of segments) {
    const len = pick(s);
    if (s.overlapsPrevious) {
      total += Math.max(0, len - previous);
    } else {
      total += len;
      previous = len;
    }
  }
  return total;
}

export function deriveTimes(t: RecipeTimeline): DerivedTimes {
  const { segments } = t;
  let handsOnMinutes = 0;
  let moments = 0;
  let inRun = false;
  let longest: TimelineSegment | null = null;

  for (const s of segments) {
    if (s.kind === 'active') {
      handsOnMinutes += s.max;
      if (!s.overlapsPrevious && !inRun) {
        moments += 1;
        inRun = true;
      }
    } else {
      inRun = false;
      if (!longest || s.max > longest.max) longest = s;
    }
  }

  return {
    handsOnMinutes,
    readyInMinutes: elapsed(segments, readyLength),
    readyInMinutesMin: elapsed(segments, s => s.min),
    moments,
    longestWait: longest
      ? {
          kind: longest.kind as 'wait-away' | 'wait-home',
          min: longest.min,
          max: longest.max,
          wait: longest.wait ?? 'other',
        }
      : null,
  };
}

export function checkTimeline(
  t: RecipeTimeline,
  ctx: { steps: unknown[]; statedTotal?: number | null },
): TimelineCheck[] {
  const checks: TimelineCheck[] = [];
  const { handsOnMinutes, readyInMinutes, longestWait } = deriveTimes(t);
  // A page's total includes the whole of a marinade, and a chosen mean says nothing
  // about a dropped wait: the page is compared with every segment at its long end.
  const longEnd = elapsed(t.segments, s => s.max);
  const stated = findStatedWaits(ctx.steps);
  const statedLongest = stated.reduce((m, w) => Math.max(m, w.max), 0);
  const keptLongest = longestWait?.max ?? 0;

  if (statedLongest >= MIN_WAIT_MINUTES && statedLongest > MISSED_WAIT_RATIO * keptLongest) {
    checks.push({
      code: 'wait_missed',
      detail: `steps state a wait of ${statedLongest} min, the timeline's longest is ${keptLongest} min`,
    });
  }

  const invented = unstatedWaits(t.segments, ctx.steps);
  if (invented.length > 0) {
    checks.push({
      code: 'wait_unstated',
      detail: invented.map(s => `step ${s.step + 1}: ${s.wait ?? s.kind} ${s.max} min`).join(', '),
    });
  }

  // « Préparation » is part of « Prêt en »: more of it than the whole means overlaps the model got wrong.
  if (handsOnMinutes > readyInMinutes) {
    checks.push({
      code: 'hands_on_exceeds_ready',
      detail: `hands-on ${handsOnMinutes} min, ready in ${readyInMinutes} min`,
    });
  }

  const total = ctx.statedTotal;
  if (total != null) {
    if (longEnd < total - UNDER_STATED_SLACK_MIN) {
      checks.push({
        code: 'under_stated',
        detail: `long end ${longEnd} min, the page says ${total} min`,
      });
    }
    if (stated.length === 0 && readyInMinutes > Math.max(OVER_FACTOR * total, total + OVER_SLACK_MIN)) {
      checks.push({
        code: 'over_without_wait',
        detail: `ready in ${readyInMinutes} min against ${total} min, and no step states a wait`,
      });
    }
  }

  return checks;
}
